import {
  type Step,
  type Lesson,
  type LessonStepPlayerItem,
  type EnhancerAnswer,
  type EnhancerChoice,
  LessonStepPlayerItemType,
  EnhancerFunctionalType,
  LessonMode,
} from '@/models/Lesson';
import { ActivityStatus, ActivityType } from '@/models/Activity';
import { ContentType, RemoteScormCommand, type RemoteScormMessage } from '@/models/Session';
import { LoginOrigin } from '@/models/User';
import { Milestone } from '@/models/Milestone';

import { computed, ref, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';

import { getLessonMilestones, scrollIntoLessonStep } from '@/utils/lesson';

import { track } from '@/analytics/mixpanel';

import { useSessionStorage } from '@/composables/useStorage';

import { useSessionStore } from './useSessionStore';
import { useContentStore } from './useContentStore';
import { useNavigationStore } from './useNavigationStore';

export const useLessonStore = defineStore('lesson', () => {
  const milestoneLogs = ref<Milestone[]>([]);
  const activeLessonStep = ref<Step>();
  const activeLessonStepIndex = ref<number>(0);
  const activeLessonStepPlayer = ref<LessonStepPlayerItem[]>();
  const activeLessonStepPlayerItem = ref<LessonStepPlayerItem>();
  const isLastLessonStepPlayerItem = ref<boolean>(false);
  const isLessonCompleted = ref<boolean>();
  const isFeedbackModalVisible = ref<boolean>(false);
  const shouldShowFeedbackModal = ref<boolean>(false);
  const shouldTrackLessonCompletion = ref<boolean>(false);

  const sessionStore = useSessionStore();

  const { contentData } = storeToRefs(useContentStore());

  const { redirectToContentPage, redirectToLessonFeedbackPage } = useNavigationStore();

  const { get: getEnhancerAnswers } = useSessionStorage('boosterAnswers');
  const { get: getTrackedEnhancerAnswers, set: setTrackedEnhancerAnswers } =
    useSessionStorage('trackedEnhancerAnswers');

  const lessonContent = computed(() => contentData.value as Lesson);

  const initialize = async () => {
    if (sessionStore.contentType !== ContentType.lesson) return;

    const isCompleted = await getLessonCompletionStatus();

    sessionStore.setLessonMode(isCompleted ? LessonMode.cherryPick : LessonMode.story);

    window.document.title = lessonContent.value.title;

    window.addEventListener('beforeunload', handleRemoteScormFinish);
  };

  const getEnhancerAnswer = (enhancerId: string) => {
    const answers: EnhancerAnswer[] = getEnhancerAnswers();
    if (!answers) return null;

    const answer = answers.find((answer: EnhancerAnswer) => answer.id === enhancerId);
    if (!answer) return null;

    const trackedEnhancerAnswers: string[] = getTrackedEnhancerAnswers() ?? [];

    return { answer, isTracked: trackedEnhancerAnswers.includes(enhancerId) };
  };

  const createDropdownEnhancerAnswer = (savedAnswer: EnhancerChoice) => {
    return savedAnswer.title;
  };

  const createSliderEnhancerAnswer = (savedAnswer: number, options?: EnhancerChoice[]) => {
    if (!options) return savedAnswer;

    return savedAnswer < 50 ? options[0].title : options[1].title;
  };

  const createAnswer = (savedAnswer: unknown) => {
    if (!activeLessonStepPlayerItem.value?.enhancer) return;

    switch (activeLessonStepPlayerItem.value.enhancer._type) {
      case EnhancerFunctionalType.dropdown:
        return createDropdownEnhancerAnswer(savedAnswer as EnhancerChoice);
      case EnhancerFunctionalType.slider:
        return createSliderEnhancerAnswer(
          savedAnswer as number,
          activeLessonStepPlayerItem.value.enhancer.options as EnhancerChoice[],
        );
      default:
        return savedAnswer as string;
    }
  };

  const trackLessonEnhancerAnswer = () => {
    if (!activeLessonStepPlayerItem.value?.enhancer) return;

    const answerData = getEnhancerAnswer(activeLessonStepPlayerItem.value.enhancer._key);
    if (!answerData || answerData.isTracked) return;

    track('Lesson Enhancer Answer', {
      enhancer_type: activeLessonStepPlayerItem.value.enhancer._type,
      lesson_name: lessonContent.value?.title,
      lesson_step_name: activeLessonStep.value?.title,
      question: activeLessonStepPlayerItem.value.enhancer.title,
      answer: createAnswer(answerData.answer.data),
    });

    const trackedEnhancerAnswers: string[] = getTrackedEnhancerAnswers() ?? [];
    if (!trackedEnhancerAnswers.includes(answerData.answer.id)) {
      trackedEnhancerAnswers.push(answerData.answer.id);
      setTrackedEnhancerAnswers(JSON.stringify(trackedEnhancerAnswers));
    }
  };

  const setActiveLessonStep = (step: Step) => {
    if (!lessonContent.value) return;

    activeLessonStepIndex.value = lessonContent.value.steps.indexOf(step) || 0;
    activeLessonStep.value = step;

    setActiveLessonStepPlayer();
    nextActiveLessonStepPlayerItem();
  };

  const setActiveLessonStepPlayer = () => {
    if (!lessonContent.value || !activeLessonStep.value) return;

    const player: LessonStepPlayerItem[] = [];

    lessonContent.value.enhancers
      ?.filter((enhancer) =>
        [enhancer.startPosition, enhancer.endPosition].includes(
          (activeLessonStepIndex.value + 1) * 2 - 1,
        ),
      )
      ?.forEach(({ enhancer }) => {
        player.push({
          type: LessonStepPlayerItemType.enhancer,
          enhancer: enhancer[0],
        });
      });

    activeLessonStep.value.primerEnhancers?.forEach((enhancer) => {
      player.push({ type: LessonStepPlayerItemType.enhancer, enhancer });
    });

    player.push({
      type: LessonStepPlayerItemType.activity,
      activity: activeLessonStep.value.activity,
    });

    activeLessonStep.value.recapEnhancers?.forEach((enhancer) => {
      player.push({ type: LessonStepPlayerItemType.enhancer, enhancer });
    });

    lessonContent.value.enhancers
      ?.filter((enhancer) =>
        [enhancer.startPosition, enhancer.endPosition].includes(
          (activeLessonStepIndex.value + 1) * 2,
        ),
      )
      ?.forEach(({ enhancer }) => {
        player.push({
          type: LessonStepPlayerItemType.enhancer,
          enhancer: enhancer[0],
          isOutput: true,
        });
      });

    activeLessonStepPlayer.value = player;
  };

  const nextActiveLessonStepPlayerItem = (shouldClose = false) => {
    if (!lessonContent.value || !activeLessonStepPlayer.value) return;

    if (!activeLessonStepPlayerItem.value) {
      activeLessonStepPlayerItem.value = activeLessonStepPlayer.value[0];
      return;
    }

    trackLessonEnhancerAnswer();

    const playerLength = activeLessonStepPlayer.value.length;
    const currentIndex = activeLessonStepPlayer.value.indexOf(activeLessonStepPlayerItem.value);
    const nextIndex = currentIndex + 1;

    const isFinished = nextIndex >= playerLength;
    if (isFinished) {
      finishLessonStep();
      return;
    }

    const nextLessonStepPlayerItem = activeLessonStepPlayer.value[nextIndex];

    if (
      shouldClose &&
      !nextLessonStepPlayerItem.enhancer?.alwaysShow &&
      activeLessonStepPlayerItem.value.activity?.activityType !== ActivityType.ghccActivity
    ) {
      redirectToContentPage();
      return;
    }

    activeLessonStepPlayerItem.value = nextLessonStepPlayerItem;
    isLastLessonStepPlayerItem.value = nextIndex + 1 === playerLength;
  };

  const getLessonCompletionStatus = async (): Promise<boolean> => {
    milestoneLogs.value = await getLessonMilestones(lessonContent.value.id);

    const score =
      (lessonContent.value.steps.filter((step) => step.activity.status === ActivityStatus.completed)
        .length /
        lessonContent.value.steps.length) *
      100;

    handleRemoteScormUpdateScore(score);

    const isCompleted = score === 100;

    isLessonCompleted.value = isCompleted;

    return isCompleted;
  };

  const setLessonStatus = async (shouldTrack = false) => {
    const isCompleted = await getLessonCompletionStatus();

    if (isCompleted && shouldTrack) {
      track('Lesson Complete', { lesson_name: lessonContent.value.title });

      shouldShowFeedbackModal.value = true;
    }
  };

  const finishLessonStep = () => {
    if (!lessonContent.value) return;

    isLastLessonStepPlayerItem.value = false;

    const nextLessonStepId = lessonContent.value.steps[activeLessonStepIndex.value + 1]?.id || '';

    redirectToContentPage();

    if (nextLessonStepId) {
      setTimeout(() => {
        scrollIntoLessonStep(nextLessonStepId);
      }, 150);
    }

    if (shouldShowFeedbackModal.value) {
      shouldShowFeedbackModal.value = false;

      redirectToLessonFeedbackPage();
    }
  };

  const handleRemoteScormUpdateScore = (score: number) => {
    if (
      !sessionStore.platform ||
      sessionStore.platform.toLowerCase() !== LoginOrigin.RS.toLowerCase()
    )
      return;

    const remoteScormUpdateScoreMessage: RemoteScormMessage = {
      Command: RemoteScormCommand.updateScore,
      Score: score,
    };

    window.parent.postMessage(
      remoteScormUpdateScoreMessage,
      import.meta.env.VITE_LMS_CONNECTOR_API,
    );
  };

  const handleRemoteScormFinish = () => {
    if (
      !sessionStore.platform ||
      sessionStore.platform.toLowerCase() !== LoginOrigin.RS.toLowerCase()
    )
      return;

    const remoteScormFinishMessage: RemoteScormMessage = {
      Command: RemoteScormCommand.finish,
    };

    window.parent.postMessage(remoteScormFinishMessage, import.meta.env.VITE_LMS_CONNECTOR_API);
  };

  watch(
    () => lessonContent.value,
    () => initialize(),
  );

  watch(
    isLessonCompleted,
    (isCompleted) => {
      if (isCompleted === false) {
        shouldTrackLessonCompletion.value = true;
      } else if (isCompleted && shouldTrackLessonCompletion.value) {
        track('Lesson Complete', { lesson_name: lessonContent.value.title });
        shouldShowFeedbackModal.value = true;
        shouldTrackLessonCompletion.value = false;
      }
    },
    { immediate: true },
  );

  return {
    lessonContent,
    milestoneLogs,
    isLessonCompleted,
    activeLessonStep,
    activeLessonStepIndex,
    activeLessonStepPlayer,
    activeLessonStepPlayerItem,
    isLastLessonStepPlayerItem,
    isFeedbackModalVisible,
    setLessonStatus,
    setActiveLessonStep,
    setActiveLessonStepPlayer,
    nextActiveLessonStepPlayerItem,
    handleRemoteScormUpdateScore,
  };
});
